/**
 * Maps the API-level `EmployeeChangeRequest` onto the display-oriented
 * `DetailUpdateRequest` the shared request cards render.
 *
 * Both portals list the same rows — the employee their own, the company
 * everyone's — so the mapping lives here and cannot drift between them. Field
 * labels come from the shared form's own translation keys, so a card and the
 * form behind it always name a field the same way.
 */

import type { TFunction } from 'i18next';
import {
  DetailUpdateAttachmentSide,
  DetailUpdateRequestStatus,
  type DetailUpdateRequest,
  type DetailUpdateRequestAttachment,
  type DetailUpdateRequestField,
} from '@gokrew/models/detail-update-requests';
import {
  EmployeeChangeRequestAction,
  EmployeeChangeRequestStatus,
  EmployeeSection,
  type EmployeeChangeRequest,
} from '@gokrew/models/employee-change-requests';
import { Tone } from '@gokrew/models/ui.types';
import type {
  EmployeeRecordHistoryEntry,
  EmployeeRecordHistoryField,
} from '@gokrew/models/employee-records';
import { getEducationLevelLabel } from './educationLevel';
import { formatPhone } from './phone';
import { formatDate, getDisplayFileName } from './utility';

const DISPLAY_DATE_FORMAT = 'D MMM YYYY';

/** A history entry names the moment, so two updates on one day read in order. */
const DISPLAY_DATE_TIME_FORMAT = 'D MMM YYYY · h:mm A';

export const CHANGE_REQUEST_STATUS_MAP: Record<
  EmployeeChangeRequestStatus,
  DetailUpdateRequestStatus
> = {
  [EmployeeChangeRequestStatus.Pending]: DetailUpdateRequestStatus.Pending,
  [EmployeeChangeRequestStatus.Approved]: DetailUpdateRequestStatus.Approved,
  [EmployeeChangeRequestStatus.Rejected]: DetailUpdateRequestStatus.Rejected,
  [EmployeeChangeRequestStatus.Withdrawn]: DetailUpdateRequestStatus.Withdrawn,
  [EmployeeChangeRequestStatus.Applied]: DetailUpdateRequestStatus.Applied,
};

/** The chip tone each display status wears, wherever it is rendered. */
export const DETAIL_UPDATE_STATUS_TONE: Record<
  DetailUpdateRequestStatus,
  Tone
> = {
  [DetailUpdateRequestStatus.Pending]: Tone.WARNING,
  [DetailUpdateRequestStatus.Approved]: Tone.SUCCESS,
  [DetailUpdateRequestStatus.Rejected]: Tone.ERROR,
  [DetailUpdateRequestStatus.Withdrawn]: Tone.SECONDARY,
  [DetailUpdateRequestStatus.Applied]: Tone.SOFT_BLUE,
};

/** API field name → the shared form's label key, which the card reuses. */
const EDUCATION_FIELD_LABEL_KEYS: Record<string, string> = {
  educationLevel: 'education_level',
  institutionName: 'institution_name',
  course: 'course',
  startDate: 'start_date',
  endDate: 'end_date',
};

const EDUCATION_DATE_FIELDS = new Set(['startDate', 'endDate']);

const FAMILY_FIELD_LABEL_KEYS: Record<string, string> = {
  firstName: 'first_name',
  lastName: 'last_name',
  phoneNumber: 'phone_number',
  isEmergencyContact: 'emergency_contact',
  name: 'name',
  relation: 'relation',
  gender: 'gender',
  email: 'email',
  dateOfBirth: 'date_of_birth',
};

/** Family values that are enum keys, not free text — each has its own labels. */
const FAMILY_ENUM_FIELDS: Record<string, string> = {
  relation: 'relations',
  gender: 'genders',
};

const DOCUMENT_FIELD_LABEL_KEYS: Record<string, string> = {
  documentTypeId: 'document_type',
  documentUrl: 'upload_file',
  expiresOn: 'expiry_date',
};

/** A document's expiry is a day (`YYYY-MM-DD`), read like any other date. */
const formatDocumentExpiry = (value: unknown, locale?: string) =>
  typeof value === 'string' && value
    ? formatDate(value, { format: DISPLAY_DATE_FORMAT, locale })
    : null;

/**
 * Whether a change left the field where it was — an empty value, an absent one
 * and `null` all count as the same nothing, so re-saving a blank field is not
 * a change either.
 */
const isUnchanged = (before: unknown, after: unknown) => {
  const normalize = (value: unknown) =>
    value === null || value === undefined || value === ''
      ? ''
      : JSON.stringify(value);

  return normalize(before) === normalize(after);
};

const TITLE_ACTION_KEYS: Record<EmployeeChangeRequestAction, string> = {
  [EmployeeChangeRequestAction.Create]: 'create',
  [EmployeeChangeRequestAction.Update]: 'update',
  [EmployeeChangeRequestAction.Delete]: 'delete',
};

/**
 * The API sends no per-request title, so one is generated: what the request
 * does, to which kind of record, and — when the request names it — which one.
 * e.g. "Added Course Details (ITI)", "Updated Document (Aadhaar Card)".
 *
 * `value` is the record's own name, taken from the diff; without it the title
 * is just the action and the record kind.
 */
const requestTitle = (
  row: EmployeeChangeRequest,
  value: string,
  sectionLabel: string,
  tLibs: TFunction
) => {
  const record = tLibs(
    `detail_update_requests.request_title.record.${row.section}`,
    { defaultValue: sectionLabel }
  );

  return tLibs(
    `detail_update_requests.request_title.${TITLE_ACTION_KEYS[row.action] ?? 'update'}`,
    {
      record: value
        ? tLibs('detail_update_requests.request_title.with_value', {
            record,
            value,
          })
        : record,
    }
  );
};

const fileNameFromUrl = getDisplayFileName;

// Education carries its files as a `documents` array; a document request
// carries the one `documentUrl` string. Both funnel through here.
const toDocumentUrls = (value: unknown): string[] => {
  if (Array.isArray(value))
    return value.filter((entry) => typeof entry === 'string');
  return typeof value === 'string' && value ? [value] : [];
};

const toAttachments = (
  urls: string[],
  side: DetailUpdateAttachmentSide
): DetailUpdateRequestAttachment[] =>
  urls.map((url, index) => ({
    id: `${side}-${index}-${url}`,
    side,
    fileName: fileNameFromUrl(url),
    url,
  }));

const formatEducationValue = (
  field: string,
  value: unknown,
  locale?: string
) => {
  if (value === null || value === undefined || value === '') return null;
  if (field === 'educationLevel') return getEducationLevelLabel(String(value));
  if (EDUCATION_DATE_FIELDS.has(field))
    return formatDate(String(value), { format: DISPLAY_DATE_FORMAT, locale });
  return String(value);
};

/**
 * Both approve (change rules, two columns): once the manager approved, the
 * card says so while it waits for HR. Nothing on a one-step request.
 */
const managerApprovalNote = (
  row: EmployeeChangeRequest,
  tLibs: TFunction,
  locale?: string
): string | undefined => {
  const approval = row.managerApproval;
  if (!approval) return undefined;
  const date = formatDate(approval.approvedAt, {
    format: DISPLAY_DATE_FORMAT,
    locale,
  });
  const line = approval.approvedBy?.name
    ? tLibs('detail_update_requests.manager_approved_by', {
        name: approval.approvedBy.name,
        date,
      })
    : tLibs('detail_update_requests.manager_approved', { date });
  return approval.note ? `${line} ${approval.note}` : line;
};

/** Options every request mapper takes. */
export interface ChangeRequestMapOptions {
  /** The `libsCommon` translator, which owns the shared form's field labels. */
  tLibs: TFunction;
  /**
   * The active `i18n.language`. Dates are month names, so a card rendered
   * without it prints English months inside an otherwise French page.
   */
  locale?: string;
}

export interface EducationChangeRequestMapOptions extends ChangeRequestMapOptions {
  /** Already-translated section name — each portal names it from its own namespace. */
  sectionLabel: string;
}

export const toEducationDetailUpdateRequest = (
  row: EmployeeChangeRequest,
  { sectionLabel, tLibs, locale }: EducationChangeRequestMapOptions
): DetailUpdateRequest => {
  const fields: DetailUpdateRequestField[] = [];
  const attachments: DetailUpdateRequestAttachment[] = [];

  const changedValue = (field: string) => {
    const change = row.changes?.find((entry) => entry.field === field);
    return change?.after ? String(change.after) : '';
  };

  row.changes?.forEach((change) => {
    if (change.field === 'documents') {
      attachments.push(
        ...toAttachments(
          toDocumentUrls(change.before),
          DetailUpdateAttachmentSide.Current
        ),
        ...toAttachments(
          toDocumentUrls(change.after),
          DetailUpdateAttachmentSide.Requested
        )
      );
      return;
    }

    const labelKey = EDUCATION_FIELD_LABEL_KEYS[change.field];
    if (!labelKey) return;

    fields.push({
      id: change.field,
      label: tLibs(`detail_update_requests.form.fields.${labelKey}`),
      from: formatEducationValue(change.field, change.before),
      to: formatEducationValue(change.field, change.after),
    });
  });

  return {
    id: row.id,
    section: row.section,
    sectionLabel,
    // The course names the entry on the card; the institution stands in when
    // the request does not carry one.
    subject: requestTitle(
      row,
      changedValue('course') || changedValue('institutionName'),
      sectionLabel,
      tLibs
    ),
    status:
      CHANGE_REQUEST_STATUS_MAP[row.status] ??
      DetailUpdateRequestStatus.Pending,
    fields,
    attachments,
    reason: row.reasonForChange ?? undefined,
    decisionNote: row.reason ?? row.reviewerNote ?? undefined,
    managerApprovalNote: managerApprovalNote(row, tLibs, locale),
    submittedAt: formatDate(row.submittedAt, {
      format: DISPLAY_DATE_FORMAT,
      locale,
    }),
  };
};

export interface DocumentChangeRequestMapOptions extends EducationChangeRequestMapOptions {
  /**
   * Resolves a document type id to its name — the request carries the id, and
   * a raw id on a card tells the reviewer nothing. Unresolved ids fall back to
   * the id itself rather than vanishing from the diff.
   */
  resolveDocumentTypeName?: (id: string) => string | undefined;
}

/**
 * A document request's diff: the type it is filed under, and the file itself —
 * shown both as a filename change and as the before/after preview tiles.
 *
 * `extraData` is deliberately not expanded: its keys are per-document-type and
 * carry no labels, and neither portal's document form writes it today.
 */
export const toDocumentDetailUpdateRequest = (
  row: EmployeeChangeRequest,
  {
    sectionLabel,
    tLibs,
    locale,
    resolveDocumentTypeName,
  }: DocumentChangeRequestMapOptions
): DetailUpdateRequest => {
  const fields: DetailUpdateRequestField[] = [];
  const attachments: DetailUpdateRequestAttachment[] = [];
  const documentTypeName = (value: unknown) => {
    if (typeof value !== 'string' || !value) return null;
    return resolveDocumentTypeName?.(value) ?? value;
  };

  let subject = '';

  row.changes?.forEach((change) => {
    if (change.field === 'documentUrl') {
      const [currentUrl] = toDocumentUrls(change.before);
      const [requestedUrl] = toDocumentUrls(change.after);

      attachments.push(
        ...toAttachments(
          toDocumentUrls(change.before),
          DetailUpdateAttachmentSide.Current
        ),
        ...toAttachments(
          toDocumentUrls(change.after),
          DetailUpdateAttachmentSide.Requested
        )
      );

      fields.push({
        id: change.field,
        label: tLibs('detail_update_requests.form.fields.upload_file'),
        from: currentUrl ? fileNameFromUrl(currentUrl) : null,
        to: requestedUrl ? fileNameFromUrl(requestedUrl) : null,
      });
      return;
    }

    if (change.field === 'documentTypeId') {
      const to = documentTypeName(change.after);
      subject = to ?? subject;

      fields.push({
        id: change.field,
        label: tLibs('detail_update_requests.form.fields.document_type'),
        from: documentTypeName(change.before),
        to,
      });
      return;
    }

    // Sent only when the employee set or moved it, but an update that kept
    // the same day still says nothing new, so it is left off the card.
    if (
      change.field === 'expiresOn' &&
      !isUnchanged(change.before, change.after)
    ) {
      fields.push({
        id: change.field,
        label: tLibs('detail_update_requests.form.fields.expiry_date'),
        from: formatDocumentExpiry(change.before, locale),
        to: formatDocumentExpiry(change.after, locale),
      });
    }
  });

  return {
    id: row.id,
    section: row.section,
    sectionLabel,
    // The type names the document on the card.
    subject: requestTitle(row, subject, sectionLabel, tLibs),
    status:
      CHANGE_REQUEST_STATUS_MAP[row.status] ??
      DetailUpdateRequestStatus.Pending,
    fields,
    attachments,
    reason: row.reasonForChange ?? undefined,
    decisionNote: row.reason ?? row.reviewerNote ?? undefined,
    managerApprovalNote: managerApprovalNote(row, tLibs, locale),
    submittedAt: formatDate(row.submittedAt, {
      format: DISPLAY_DATE_FORMAT,
      locale,
    }),
  };
};

/**
 * A family request's diff. Relation and gender arrive as enum keys, so both
 * sides are read through the shared form's own option labels.
 */
const formatFamilyValue = (
  field: string,
  value: unknown,
  tLibs: TFunction,
  locale?: string
) => {
  if (value === null || value === undefined || value === '') return null;
  if (field === 'dateOfBirth')
    return formatDate(String(value), { format: DISPLAY_DATE_FORMAT, locale });

  if (field === 'isEmergencyContact')
    return tLibs(
      value
        ? 'detail_update_requests.form.fields.emergency_badge'
        : 'detail_update_requests.form.fields.not_emergency'
    );

  if (field === 'phoneNumber') return formatPhone(String(value));

  const optionGroup = FAMILY_ENUM_FIELDS[field];
  if (optionGroup)
    return tLibs(
      `detail_update_requests.form.${optionGroup}.${String(value)}`,
      {
        defaultValue: String(value),
      }
    );

  return String(value);
};

export const toFamilyDetailUpdateRequest = (
  row: EmployeeChangeRequest,
  { sectionLabel, tLibs, locale }: EducationChangeRequestMapOptions
): DetailUpdateRequest => {
  const familyValue = (field: string, value: unknown) =>
    formatFamilyValue(field, value, tLibs, locale);

  const fields: DetailUpdateRequestField[] = [];
  let subject = '';

  row.changes?.forEach((change) => {
    const labelKey = FAMILY_FIELD_LABEL_KEYS[change.field];
    if (!labelKey) return;

    const to = familyValue(change.field, change.after);
    if (change.field === 'name') subject = to ?? subject;

    fields.push({
      id: change.field,
      label: tLibs(`detail_update_requests.form.fields.${labelKey}`),
      from: familyValue(change.field, change.before),
      to,
    });
  });

  return {
    id: row.id,
    section: row.section,
    sectionLabel,
    // The member's name heads the card.
    subject: requestTitle(row, subject, sectionLabel, tLibs),
    status:
      CHANGE_REQUEST_STATUS_MAP[row.status] ??
      DetailUpdateRequestStatus.Pending,
    fields,
    reason: row.reasonForChange ?? undefined,
    decisionNote: row.reason ?? row.reviewerNote ?? undefined,
    managerApprovalNote: managerApprovalNote(row, tLibs, locale),
    submittedAt: formatDate(row.submittedAt, {
      format: DISPLAY_DATE_FORMAT,
      locale,
    }),
  };
};

/** Builds the card fields shared by the bank and personal information mappers. */
const labelledField = (
  id: string,
  label: string,
  from: string | null,
  to: string | null,
  sensitive?: boolean
): DetailUpdateRequestField => ({
  id,
  label,
  from,
  to,
  ...(sensitive ? { sensitive } : {}),
});

const textValue = (value: unknown) =>
  value === null || value === undefined || value === '' ? null : String(value);

/** The bank account columns a request can carry, in the order a card lists them. */
const BANK_FIELD_LABEL_KEYS: Record<string, string> = {
  countryCode: 'country',
  bankName: 'bank_name',
  accountHolderName: 'account_holder_name',
  accountNumber: 'account_number',
};

interface BankFieldValue {
  countryBankingFieldId: string;
  fieldValue: string;
}

const bankFieldValues = (value: unknown): Map<string, string> => {
  const entries = Array.isArray(value) ? (value as BankFieldValue[]) : [];
  return new Map(
    entries
      .filter(
        (entry) => entry && typeof entry.countryBankingFieldId === 'string'
      )
      .map((entry) => [
        entry.countryBankingFieldId,
        String(entry.fieldValue ?? ''),
      ])
  );
};

export interface BankAccountChangeRequestMapOptions extends EducationChangeRequestMapOptions {
  /**
   * Names a country banking field (institution number, transit number, …).
   * The request carries only the field id; an unresolved id falls back to a
   * generic "Banking field" label rather than printing the id.
   */
  resolveBankFieldLabel?: (countryBankingFieldId: string) => string | undefined;
  /** Names a country code; falls back to the code itself. */
  resolveCountryName?: (countryCode: string) => string | undefined;
}

/**
 * A bank account request's diff. The account number is marked `sensitive`, so
 * the card masks it and only the side-by-side comparison shows it whole
 * (D57). The country banking values arrive as one `fields` array and are
 * spread out, one row per banking field. A void cheque is a requested
 * attachment: it lives on the request only, never on the account.
 */
export const toBankAccountDetailUpdateRequest = (
  row: EmployeeChangeRequest,
  {
    sectionLabel,
    tLibs,
    locale,
    resolveBankFieldLabel,
    resolveCountryName,
  }: BankAccountChangeRequestMapOptions
): DetailUpdateRequest => {
  const fields: DetailUpdateRequestField[] = [];
  const attachments: DetailUpdateRequestAttachment[] = [];
  const label = (key: string) =>
    tLibs(`detail_update_requests.form.fields.${key}`);
  let subject = '';

  row.changes?.forEach((change) => {
    if (change.field === 'voidChequeUrl') {
      const [requestedUrl] = toDocumentUrls(change.after);
      if (!requestedUrl) return;
      attachments.push(
        ...toAttachments([requestedUrl], DetailUpdateAttachmentSide.Requested)
      );
      fields.push(
        labelledField(
          change.field,
          label('void_cheque'),
          null,
          fileNameFromUrl(requestedUrl)
        )
      );
      return;
    }

    if (change.field === 'fields') {
      const before = bankFieldValues(change.before);
      const after = bankFieldValues(change.after);
      for (const id of new Set([...before.keys(), ...after.keys()])) {
        // An update stores the account's whole set of banking values (the
        // API merges the changed ones over the rest): list only what moves.
        if ((before.get(id) || null) === (after.get(id) || null)) continue;
        fields.push(
          labelledField(
            `fields.${id}`,
            resolveBankFieldLabel?.(id) ?? label('banking_field'),
            before.get(id) || null,
            after.get(id) || null
          )
        );
      }
      return;
    }

    const labelKey = BANK_FIELD_LABEL_KEYS[change.field];
    if (!labelKey) return;

    const format = (value: unknown) => {
      const text = textValue(value);
      if (!text || change.field !== 'countryCode') return text;
      return resolveCountryName?.(text) ?? text;
    };

    const to = format(change.after);
    if (change.field === 'bankName') subject = to ?? subject;

    fields.push(
      labelledField(
        change.field,
        label(labelKey),
        format(change.before),
        to,
        change.field === 'accountNumber'
      )
    );
  });

  return {
    id: row.id,
    section: row.section,
    sectionLabel,
    // The bank names the account on the card.
    subject: requestTitle(row, subject, sectionLabel, tLibs),
    status:
      CHANGE_REQUEST_STATUS_MAP[row.status] ??
      DetailUpdateRequestStatus.Pending,
    fields,
    attachments,
    reason: row.reasonForChange ?? undefined,
    decisionNote: row.reason ?? row.reviewerNote ?? undefined,
    managerApprovalNote: managerApprovalNote(row, tLibs, locale),
    submittedAt: formatDate(row.submittedAt, {
      format: DISPLAY_DATE_FORMAT,
      locale,
    }),
  };
};

/** Personal information fields, in the order a card lists them. */
const PERSONAL_FIELD_LABEL_KEYS: Record<string, string> = {
  personalEmail: 'personal_email',
  firstName: 'first_name',
  lastName: 'last_name',
  phoneNumber: 'phone_number',
  dateOfBirth: 'date_of_birth',
  gender: 'gender',
  shirtSize: 'shirt_size',
  pantsSize: 'pants_size',
  pantsLength: 'pants_length',
};

/** The permanent address arrives as one object; each part gets its own row. */
const ADDRESS_FIELD_LABEL_KEYS: Record<string, string> = {
  addressLine_1: 'address_line_1',
  addressLine_2: 'address_line_2',
  city: 'city',
  provinceId: 'province',
  postalCode: 'postal_code',
  countryCode: 'country',
};

export interface PersonalInformationChangeRequestMapOptions extends EducationChangeRequestMapOptions {
  /** Names a province id; falls back to the id itself. */
  resolveProvinceName?: (provinceId: number | string) => string | undefined;
  /** Names a country code; falls back to the code itself. */
  resolveCountryName?: (countryCode: string) => string | undefined;
}

/**
 * A personal information request's diff: name, phone, birth date, gender,
 * sizes, and the permanent address spread into one row per part. The sign-in
 * email is never part of one (D57).
 */
export const toPersonalInformationDetailUpdateRequest = (
  row: EmployeeChangeRequest,
  {
    sectionLabel,
    tLibs,
    locale,
    resolveProvinceName,
    resolveCountryName,
  }: PersonalInformationChangeRequestMapOptions
): DetailUpdateRequest => {
  const fields: DetailUpdateRequestField[] = [];
  const label = (key: string) =>
    tLibs(`detail_update_requests.form.fields.${key}`);

  const personalValue = (field: string, value: unknown) => {
    const text = textValue(value);
    if (!text) return null;
    if (field === 'phoneNumber') return formatPhone(text);
    if (field === 'dateOfBirth')
      return formatDate(text, { format: DISPLAY_DATE_FORMAT, locale });
    if (field === 'gender')
      return tLibs(`detail_update_requests.form.genders.${text}`, {
        defaultValue: text,
      });
    return text;
  };

  const addressValue = (field: string, value: unknown) => {
    const text = textValue(value);
    if (!text) return null;
    if (field === 'provinceId') return resolveProvinceName?.(text) ?? text;
    if (field === 'countryCode') return resolveCountryName?.(text) ?? text;
    return text;
  };

  row.changes?.forEach((change) => {
    if (change.field === 'address') {
      const before = (change.before ?? {}) as Record<string, unknown>;
      const after = (change.after ?? {}) as Record<string, unknown>;
      Object.entries(ADDRESS_FIELD_LABEL_KEYS).forEach(([part, key]) => {
        fields.push(
          labelledField(
            `address.${part}`,
            label(key),
            addressValue(part, before[part]),
            addressValue(part, after[part])
          )
        );
      });
      return;
    }

    const labelKey = PERSONAL_FIELD_LABEL_KEYS[change.field];
    if (!labelKey) return;

    fields.push(
      labelledField(
        change.field,
        label(labelKey),
        personalValue(change.field, change.before),
        personalValue(change.field, change.after)
      )
    );
  });

  return {
    id: row.id,
    section: row.section,
    sectionLabel,
    subject: requestTitle(row, '', sectionLabel, tLibs),
    status:
      CHANGE_REQUEST_STATUS_MAP[row.status] ??
      DetailUpdateRequestStatus.Pending,
    fields,
    reason: row.reasonForChange ?? undefined,
    decisionNote: row.reason ?? row.reviewerNote ?? undefined,
    managerApprovalNote: managerApprovalNote(row, tLibs, locale),
    submittedAt: formatDate(row.submittedAt, {
      format: DISPLAY_DATE_FORMAT,
      locale,
    }),
  };
};

/**
 * The countries named by personal information requests' addresses, on either
 * side of the change. A portal loads those countries' provinces to name the
 * `provinceId` a request carries.
 */
export const personalInformationCountryCodes = (
  rows: EmployeeChangeRequest[]
): string[] => {
  const codes = new Set<string>();
  rows.forEach((row) => {
    if (row.section !== EmployeeSection.PersonalInformation) return;
    row.changes?.forEach((change) => {
      if (change.field !== 'address') return;
      [change.before, change.after].forEach((side) => {
        const code = (side as { countryCode?: unknown } | null)?.countryCode;
        if (typeof code === 'string' && code) codes.add(code);
      });
    });
  });
  return [...codes].sort();
};

export interface RecordHistoryMapOptions extends ChangeRequestMapOptions {
  /**
   * Document history only — a change carries the document type id, and a raw
   * id in the trail tells the reader nothing.
   */
  resolveDocumentTypeName?: (id: string) => string | undefined;
}

/** The files a change put on the record, named the way the record names them. */
const fileNames = (value: unknown) =>
  toDocumentUrls(value).map(fileNameFromUrl).join(', ') || null;

/**
 * One approved request's diff as the record trail lists it: each field it
 * touched, the value that stood there before it, and the value it left
 * behind.
 *
 * A request carries every field it was submitted with, not only the ones that
 * moved, so a field whose before and after match is dropped — the trail says
 * what changed, and listing an untouched value as an update is a lie.
 */
const historyFields = (
  row: EmployeeChangeRequest,
  { tLibs, locale, resolveDocumentTypeName }: RecordHistoryMapOptions
): EmployeeRecordHistoryField[] =>
  (row.changes ?? []).flatMap((change) => {
    if (isUnchanged(change.before, change.after)) return [];

    const documentTypeName = (value: unknown) => {
      const typeId = typeof value === 'string' ? value : '';
      if (!typeId) return null;
      return resolveDocumentTypeName?.(typeId) ?? typeId;
    };

    // Each section names its fields and reads its values its own way, and both
    // sides of the change go through the same formatter — an old value the
    // trail printed differently from the new one would read as a change of its
    // own.
    let labelKey: string | undefined;
    let format: (value: unknown) => string | null;

    if (row.section === EmployeeSection.Education) {
      const isCertificates = change.field === 'documents';
      labelKey = isCertificates
        ? 'documents'
        : EDUCATION_FIELD_LABEL_KEYS[change.field];
      format = isCertificates
        ? fileNames
        : (value) => formatEducationValue(change.field, value, locale);
    } else if (row.section === EmployeeSection.Document) {
      labelKey = DOCUMENT_FIELD_LABEL_KEYS[change.field];
      if (change.field === 'documentUrl') format = fileNames;
      else if (change.field === 'expiresOn')
        format = (value) => formatDocumentExpiry(value, locale);
      else format = documentTypeName;
    } else {
      labelKey = FAMILY_FIELD_LABEL_KEYS[change.field];
      format = (value) => formatFamilyValue(change.field, value, tLibs, locale);
    }

    if (!labelKey) return [];

    return [
      {
        id: change.field,
        label: tLibs(`detail_update_requests.form.fields.${labelKey}`),
        from: format(change.before),
        value: format(change.after),
      },
    ];
  });

/** When the change landed on the record, not when it was asked for. */
const appliedAt = (row: EmployeeChangeRequest) =>
  row.reviewedAt ?? row.submittedAt;

/**
 * A record's history, from the request list narrowed to that record
 * (`?section=X&recordId=Y`).
 *
 * **Only approved requests are a record's history** — a pending or rejected
 * ask never touched the values on screen, and those live on the Requests tab
 * instead. An approved request whose diff names no field this section renders
 * is dropped rather than shown as an empty entry.
 *
 * Newest first, which is the order the dialog flags its latest entry in.
 */
export const toRecordHistoryEntries = (
  rows: EmployeeChangeRequest[],
  options: RecordHistoryMapOptions
): EmployeeRecordHistoryEntry[] =>
  rows
    .filter((row) => row.status === EmployeeChangeRequestStatus.Approved)
    .sort(
      (a, b) =>
        new Date(appliedAt(b)).getTime() - new Date(appliedAt(a)).getTime()
    )
    .map((row) => ({
      id: row.id,
      at: formatDate(appliedAt(row), {
        format: DISPLAY_DATE_TIME_FORMAT,
        locale: options.locale,
      }),
      actorName: row.reviewedBy?.name,
      fields: historyFields(row, options),
    }))
    .filter((entry) => entry.fields.length > 0);

export const countPendingChangeRequests = (rows: EmployeeChangeRequest[]) =>
  rows.filter((row) => row.status === EmployeeChangeRequestStatus.Pending)
    .length;

/**
 * The newest request per record — what a details row shows as its status, and
 * what tells it an update is already pending. Create requests carry no record,
 * so they take no part in this.
 */
export const latestChangeRequestByRecord = (rows: EmployeeChangeRequest[]) => {
  const latest = new Map<string, EmployeeChangeRequest>();

  for (const row of rows) {
    if (!row.recordId) continue;

    const held = latest.get(row.recordId);
    if (!held || Date.parse(row.submittedAt) > Date.parse(held.submittedAt)) {
      latest.set(row.recordId, row);
    }
  }

  return latest;
};

/** A record under review takes no second update — the API answers 409. */
export const hasPendingChangeRequest = (request?: EmployeeChangeRequest) =>
  request?.status === EmployeeChangeRequestStatus.Pending;
